import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';
import { api } from '../../App';
import { Button } from '../ui/button';

/**
 * TermsReacceptBanner
 *
 * Polls GET /api/terms/me/status on mount. If the signed-in user has
 * NOT accepted the current Terms version, surfaces a dismiss-proof
 * banner with a "Review and accept" CTA that POSTs /api/terms/accept.
 *
 * Hidden entirely when the user is up to date (needs_reaccept === false)
 * or when the request fails (we never want to block the app over a
 * telemetry endpoint).
 */
export default function TermsReacceptBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/terms/me/status');
        if (!cancelled) setStatus(res.data);
      } catch {
        // Silent: user is probably not signed in, or network blip.
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAccept = async () => {
    setBusy(true);
    try {
      const res = await api.post('/terms/accept');
      setStatus(res.data.status);
      toast.success('Terms accepted. Thank you.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not record acceptance, please try again.';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!status || !status.needs_reaccept) return null;

  return (
    <div
      data-testid="terms-reaccept-banner"
      className="rounded-2xl border border-[#D4AF37]/40 bg-[#FFFBF0] p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <FileText className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[#1A1A1A] font-medium">
            Our Terms of Service have been updated
          </p>
          <p className="text-sm text-stone-600 mt-1">
            Please review the new terms (effective {status.current_effective_date}) and
            confirm your acceptance to keep using Zuriya. You can read them
            {' '}
            <Link to="/terms" className="text-[#D4AF37] underline">here</Link>.
          </p>
        </div>
      </div>
      <Button
        onClick={handleAccept}
        disabled={busy}
        className="bg-[#1A1A1A] text-white hover:bg-black whitespace-nowrap"
        data-testid="terms-reaccept-btn"
      >
        {busy ? (
          <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Recording...</>
        ) : (
          'Review and accept'
        )}
      </Button>
    </div>
  );
}
